import { Document, type StyleDecorators } from '@avensia/excite-contentful';
import { styled } from '@glitz/react';
import { FocalPoint } from 'webapp/blocks/ImageWithFocalPoints';
import * as React from 'react';
import Image from 'webapp/components/image';
import Link from 'webapp/components/Link';
import { useViewEventTrigger } from 'webapp/tracking';
import { addViewPromotionToDataLayer, addSelectPromotionToDataLayer } from 'webapp/tracking/events/promotions';
import { createMinLargeViewportDecorator } from 'webapp/viewport';

export enum AdSize {
  Large,
  Medium,
  Small,
}

function adSizeDecorator(size: AdSize) {
  switch (size) {
    case AdSize.Large:
      return styled(
        {
          gridColumnStart: 'span 3',
        },
        contentDecorator,
      );
    case AdSize.Medium:
      return styled(
        {
          gridColumnStart: 'span 2',
          display: 'flex',
          alignItems: 'center',
        },
        contentDecorator,
      );
    case AdSize.Small:
      return styled({ gridColumnStart: 'span 1' }, contentDecorator);
  }
}

function contentSizeDecorator(size: AdSize) {
  switch (size) {
    case AdSize.Large:
      return styled({ padding: { xy: t => t.spacing.xxl } });
    case AdSize.Medium:
      return styled({ padding: { xy: t => t.spacing.xl } });
    case AdSize.Small:
      return styled({ padding: { xy: t => t.spacing.l } });
  }
}

function getImageUrl(ad: NordicNest.Api.AdApiModel, size: AdSize, isSmallViewport?: boolean) {
  switch (size) {
    case AdSize.Large:
      return ad.largeImage;
    case AdSize.Medium:
      return isSmallViewport ? (ad.mediumImage ?? ad.largeImage) : ad.mediumImage;
    case AdSize.Small:
      return ad.smallImage;
  }
}

const AdBlockContext = React.createContext<AdSize | undefined>(undefined);

export function useAdSize() {
  return React.useContext(AdBlockContext);
}

type PropType = { size: AdSize; isSmallViewport?: boolean } & NordicNest.Api.AdApiModel;
export default function Ad({ size, isSmallViewport, ...ad }: PropType) {
  const containerRef = React.useRef<HTMLDivElement>(null);

  const gradientDecorator = React.useMemo(
    () =>
      styled({
        backgroundImage: `linear-gradient(0deg, transparent 0%, rgba(0,0,0,${ad.gradientOpacity}) 50%, transparent 100%)`,
      }),
    [ad.gradientOpacity],
  );

  const points = () => {
    switch (size) {
      case AdSize.Large:
        return ad.largeViewPointsApiModel ?? [];
      case AdSize.Medium:
        return ad.mediumViewPointsApiModel ?? [];
      case AdSize.Small:
        return ad.smallViewPointsApiModel ?? [];
    }
  };

  const self = React.createRef<HTMLLIElement>();
  useViewEventTrigger(self, addViewPromotionToDataLayer, {
    content: ad,
    items: points()
      .filter(p => p.product)
      .map(p => p.product),
  });

  const content = (
    <>
      <Image image={getImageUrl(ad, size, isSmallViewport)} />
      <Content css={styled(contentSizeDecorator(size), gradientDecorator)}>
        <Document styleDecorators={styles} elements={ad.content} />
      </Content>
    </>
  );

  return (
    <AdBlockContext.Provider value={size}>
      <ListItem css={adSizeDecorator(size)} ref={self}>
        {points().length > 0 ? (
          <ContentWrapper ref={containerRef}>
            {content}
            {points().map((p, i) =>
              p.product ? (
                <FocalPoint
                  key={p.product?.url + i}
                  {...p}
                  containerRef={containerRef}
                  content={ad as NordicNest.Api.AdApiModel}
                />
              ) : null,
            )}
          </ContentWrapper>
        ) : (
          <Base
            linkIdentifier={ad.ticket}
            {...ad.url}
            onClick={() => addSelectPromotionToDataLayer({ content: ad })}
          >
            {content}
          </Base>
        )}
      </ListItem>
    </AdBlockContext.Provider>
  );
}

const styles: StyleDecorators = {
  p: styled(
    createMinLargeViewportDecorator({
      marginBottom: 0,
    }),
    {
      margin: {
        bottom: 0,
      },
      textAlign: 'center',
      lineHeight: 'normal',
    },
  ),
};

const contentDecorator = styled({
  textAlign: 'center',
  fontFamily: t => t.fontFamily?.secondary,
});

const Base = styled(Link, {
  position: 'relative',
  display: 'block',
});

const ContentWrapper = styled.div({
  position: 'relative',
});

const Content = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  color: 'white',
});

const ListItem = styled.li();
